import { axiosTassoQuintoApiInstance } from '../tasso-quinto';
import {
  ManagedLeadDropReason,
  ManagedLeadListResponse,
  ManagedLeadProductPath,
  ManagedLeadStage,
} from './managedLeads';

/**
 * The Deal as HubSpot has it right now, read while serving the queue. It is
 * `null` when the Deal no longer exists or left the product pipeline, and it is
 * absent from the decision responses: only the queue reads HubSpot.
 */
export interface ManagedDropReviewDeal {
  id: string;
  name: string | null;
  ownerId: string | null;
  /** Email of the HubSpot owner currently assigned to the Deal. */
  ownerEmail: string | null;
  /** HubSpot `createdate` of the Deal. */
  createdAt: string | null;
  stage: ManagedLeadStage;
}

/** Who asked for the Drop, as the audit distinguishes them. */
export interface ManagedDropReviewRequester {
  actorId: string | null;
  effectiveUserId: string | null;
  ownerId: string | null;
}

export interface ManagedDropReviewDecision {
  at: string | null;
  actorId: string | null;
  effectiveUserId: string | null;
  /** Only a rejection carries one; it never becomes a HubSpot Note. */
  motivation: string | null;
}

export interface ManagedDropReviewRequest {
  id: string;
  product: string;
  dealId: string;
  status: string;
  reason: ManagedLeadDropReason | null;
  /** Stage id the lead sat on when the Drop was asked for. */
  stageAtRequest: string;
  requestedAt: string | null;
  requestedBy: ManagedDropReviewRequester;
  /** `true` on `pending` alone: no other state answers Approva or Rifiuta. */
  actionable: boolean;
  /**
   * An approval that already began: HubSpot may have applied the Drop while the
   * local save failed. The request is still pending, but a retry completes the
   * local state instead of writing to HubSpot a second time.
   */
  approvalInFlight: boolean;
  decision: ManagedDropReviewDecision;
  deal?: ManagedDropReviewDeal | null;
}

export interface ManagedDropReviewListParams {
  /** One or more of the four review states, comma separated. */
  status?: string;
  search?: string;
  page: number;
  limit: 25;
}

export const listManagedDropRequests = async (
  product: ManagedLeadProductPath,
  params: ManagedDropReviewListParams
): Promise<ManagedLeadListResponse<ManagedDropReviewRequest>> => {
  const response = await axiosTassoQuintoApiInstance.get<
    ManagedLeadListResponse<ManagedDropReviewRequest>
  >(`/v1/backoffice/${product}/drop-requests`, { params });

  return response.data;
};

/**
 * Approves a pending request. There is no body: the decision carries nothing
 * the server does not already hold, and the server re-reads the Deal, applies
 * stage and reason in one HubSpot mutation and only then records `approved`.
 */
export const approveManagedDropRequest = async (
  product: ManagedLeadProductPath,
  requestId: string
): Promise<ManagedDropReviewRequest> => {
  const response =
    await axiosTassoQuintoApiInstance.post<ManagedDropReviewRequest>(
      `/v1/backoffice/${product}/drop-requests/${encodeURIComponent(
        requestId
      )}/approve`
    );

  return response.data;
};

/**
 * Rejects a pending request. Purely local: the motivation stays in the history
 * and is never written to HubSpot, and the Deal is free to receive a new
 * request straight away.
 */
export const rejectManagedDropRequest = async (
  product: ManagedLeadProductPath,
  requestId: string,
  motivation: string
): Promise<ManagedDropReviewRequest> => {
  const response =
    await axiosTassoQuintoApiInstance.post<ManagedDropReviewRequest>(
      `/v1/backoffice/${product}/drop-requests/${encodeURIComponent(
        requestId
      )}/reject`,
      { motivation }
    );

  return response.data;
};
